/**
 * React Query Hooks for Recursos (Resource Management)
 *
 * Custom hooks for fetching and mutating resource management data.
 * Follows existing pattern from project services.
 *
 * Usage:
 * ```tsx
 * const { data, isLoading } = useRecursosDashboard(projectId);
 * ```
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { recursosService } from '../end-points/recursos';
import { toast } from '@/hooks/use-toast';
import { TransactionFilter } from '@/types/recursos-dashboard.interface';
import {
  FinancingPlanItem,
  InvestmentContractItem,
  ResourceComposition
} from '@/types/gestao-recursos.interface';

/**
 * Query Keys for recursos queries
 * Centralizes cache key management
 */
export const recursosKeys = {
  all: ['recursos'] as const,
  dashboard: (projectId: string) => [...recursosKeys.all, 'dashboard', projectId] as const,
  projects: () => [...recursosKeys.all, 'projects'] as const,
  projectDetail: (projectId: string) => [...recursosKeys.all, 'detail', projectId] as const,
  transactions: (projectId: string, filters?: TransactionFilter) =>
    [...recursosKeys.all, 'transactions', projectId, filters] as const,
  monthlyStats: (projectId: string, months: number) =>
    [...recursosKeys.all, 'monthly-stats', projectId, months] as const,
  compactCharts: (projectId: string) =>
    [...recursosKeys.all, 'compact-charts', projectId] as const,
};

/**
 * Fetch dashboard overview data for a project
 * Includes balance summary, transactions, and statistics
 *
 * @param projectId - Unique project identifier
 * @returns Query result with dashboard data
 */
export function useRecursosDashboard(projectId: string) {
  return useQuery({
    queryKey: recursosKeys.dashboard(projectId),
    queryFn: async () => await recursosService.getDashboardData(projectId),
    enabled: !!projectId, // Only run query if projectId exists
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });
}

/**
 * Fetch available projects for the current user
 * Used in project selector dropdown
 *
 * @returns Query result with array of projects
 */
export function useAvailableProjects() {
  return useQuery({
    queryKey: recursosKeys.projects(),
    queryFn: async () => {
      const response = await recursosService.getAvailableProjects();
      return response.data;
    },
    staleTime: 10 * 60 * 1000, // Projects don't change often, cache for 10 minutes
  });
}

/**
 * Fetch detailed resource information for a project
 * Includes complete budget breakdown and financing details
 *
 * @param projectId - Unique project identifier
 * @returns Query result with detailed resource data
 */
export function useProjectResources(projectId: string) {
  return useQuery({
    queryKey: recursosKeys.projectDetail(projectId),
    queryFn: async () => await recursosService.getProjectResources(projectId),
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch transactions for a project with optional filters
 *
 * @param projectId - Unique project identifier
 * @param filters - Optional transaction filters
 * @returns Query result with transaction list
 */
export function useTransactions(projectId: string, filters?: TransactionFilter) {
  return useQuery({
    queryKey: recursosKeys.transactions(projectId, filters),
    queryFn: async () => {
      const response = await recursosService.getTransactions(projectId, filters);
      return response.data;
    },
    enabled: !!projectId,
    staleTime: 2 * 60 * 1000, // Transactions change frequently, shorter cache
  });
}

/**
 * Fetch monthly statistics for charts
 *
 * @param projectId - Unique project identifier
 * @param months - Number of months to retrieve (default: 7)
 * @returns Query result with monthly statistics array
 */
export function useMonthlyStatistics(projectId: string, months: number = 7) {
  return useQuery({
    queryKey: recursosKeys.monthlyStats(projectId, months),
    queryFn: async () => {
      const response = await recursosService.getMonthlyStatistics(projectId, months);
      return response.data;
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch compact chart data for dashboard sidebar
 *
 * @param projectId - Unique project identifier
 * @returns Query result with compact charts object
 */
export function useCompactCharts(projectId: string) {
  return useQuery({
    queryKey: recursosKeys.compactCharts(projectId),
    queryFn: async () => {
      const response = await recursosService.getCompactCharts(projectId);
      return response.data;
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Mutation: Update financing plan for a project
 *
 * @returns Mutation object for updating financing plan
 */
export function useUpdateFinancingPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, data }: { projectId: string; data: FinancingPlanItem[] }) =>
      recursosService.updateFinancingPlan(projectId, data),
    onSuccess: (_, variables) => {
      // Invalidate related queries to refetch updated data
      queryClient.invalidateQueries({ queryKey: recursosKeys.projectDetail(variables.projectId) });
      queryClient.invalidateQueries({ queryKey: recursosKeys.dashboard(variables.projectId) });

      toast({
        title: 'Sucesso',
        description: 'Plano de financiamento atualizado com sucesso',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro',
        description: error?.message || 'Erro ao atualizar plano de financiamento',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Mutation: Update investment contracts for a project
 *
 * @returns Mutation object for updating investment contracts
 */
export function useUpdateInvestmentContracts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, data }: { projectId: string; data: InvestmentContractItem[] }) =>
      recursosService.updateInvestmentContracts(projectId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: recursosKeys.projectDetail(variables.projectId) });
      queryClient.invalidateQueries({ queryKey: recursosKeys.dashboard(variables.projectId) });

      toast({
        title: 'Sucesso',
        description: 'Contratos de investimento atualizados com sucesso',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro',
        description: error?.message || 'Erro ao atualizar contratos de investimento',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Mutation: Update complete resource composition
 *
 * @returns Mutation object for updating resource composition
 */
export function useUpdateResourceComposition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, data }: { projectId: string; data: ResourceComposition }) =>
      recursosService.updateResourceComposition(projectId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: recursosKeys.projectDetail(variables.projectId) });
      queryClient.invalidateQueries({ queryKey: recursosKeys.dashboard(variables.projectId) });

      toast({
        title: 'Sucesso',
        description: 'Composição de recursos atualizada com sucesso',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro',
        description: error?.message || 'Erro ao atualizar composição de recursos',
        variant: 'destructive',
      });
    },
  });
}
